<template>
  <div>
    <div v-if="loading" class="dw-loading">
      <b-spinner small></b-spinner> Loading...
    </div>
    <div v-else-if="loadError" class="dw-empty">Could not load ops events.</div>
    <template v-else>
      <!-- the three workable states, from the list endpoint's first-page
           aggregate counts, so they never cap at the page size. chips rather
           than a stat-tile grid: the tiles were labeled numbers you click
           through on, and three of them cost a third of the card's height
           before the first event row. the chip carrying the rows below is
           ringed -- it is what the table's Status column used to say. -->
      <div class="dw-chip-row">
        <button
          v-for="status in statusChips"
          :key="status.key"
          type="button"
          class="dw-chip"
          :class="[status.chip, { 'dw-chip--zero': !status.count, 'dw-chip--current': status.current }]"
          :title="status.current ? ('Listed below: ' + status.label) : ('Show ' + status.label + ' events')"
          @click="goToList(status.key)"
        >{{ status.label }}<span class="dw-chip-count">{{ status.count }}</span></button>
      </div>

      <div v-if="!openRows.length" class="dw-empty">Nothing needs a response right now.</div>
      <div v-else class="dw-table-wrap">
        <table class="dw-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Category</th>
              <th>What Happened</th>
            </tr>
          </thead>
          <tbody>
            <!-- the row wears its status as a left edge in the same hue the
                 chip above uses, which is what the dropped Status column
                 said in words -->
            <tr
              v-for="row in openRows"
              :key="row.id"
              :class="row.rowClass"
              :title="row.statusLabel"
              @click="goToEvent(row.id)"
            >
              <td>#{{ row.id }}</td>
              <td>{{ row.categoryLabel }}</td>
              <!-- a description rarely fits one line, so this cell wraps to
                   three and clamps; the title still puts the whole thing one
                   hover away rather than making the row a dead end -->
              <td class="dw-cell-wrap" :title="row.what || ''">
                <span class="dw-clamp-3">{{ row.what || '—' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import { list_ops_desk_events } from '../../networking/apis'

const MAX_ROWS = 5

// matches the ops response manager list's status language; resolved rows
// never render here (the widget is a response queue, not a history). one tone
// per status drives both surfaces that carry it -- the chip above the table
// and the row edge inside it -- so they cannot drift to different hues.
const STATUS_TAGS = {
  new: { label: 'New', tone: 'danger' },
  in_progress: { label: 'In Progress', tone: 'warning' },
  awaiting_followup: { label: 'Awaiting Follow-up', tone: 'neutral' },
}
const OPEN_STATUSES = Object.keys(STATUS_TAGS)
const DEFAULT_STATUS = 'new'

function statusTag(status) {
  return STATUS_TAGS[status] || STATUS_TAGS[DEFAULT_STATUS]
}

export default {
  name: 'widget_ops_events',
  props: {
    tenant: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      events: [],
      counts: null,
      // which queue the listed rows came from -- the rows are all one status
      // (see load), which is why they no longer carry a Status column
      rowStatus: null,
    }
  },
  computed: {
    statusChips() {
      return OPEN_STATUSES.map(key => ({
        key,
        label: STATUS_TAGS[key].label,
        chip: 'dw-chip-' + STATUS_TAGS[key].tone,
        count: this.statusCount(key),
        current: this.rowStatus === key,
      }))
    },
    openRows() {
      return this.events
        .slice(0, MAX_ROWS)
        .map(e => ({
          id: e.id,
          categoryLabel: this.readableLabel(e.category || e.entry_type),
          what: (e.what_happened || '').trim(),
          // read per row rather than taken from rowStatus: the query asks for
          // one status, but a row is coded by the status it actually carries
          statusLabel: statusTag(e.status).label,
          rowClass: 'dw-row-' + statusTag(e.status).tone,
        }))
    },
  },
  created() {
    this.load()
  },
  methods: {
    statusCount(status) {
      const byStatus = (this.counts && this.counts.status) || {}
      return byStatus[status] || 0
    },
    // enum key -> display words, same treatment the orm list gives its chips
    readableLabel(value) {
      if (!value) return '—'
      return String(value)
        .split('_')
        .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ')
    },
    goToEvent(id) {
      this.$router.push({ name: 'ops_desk', query: { event_id: id } }).catch(() => {})
    },
    goToList(status) {
      this.$router.push({ name: 'ops_desk', query: { status } }).catch(() => {})
    },
    async load() {
      this.loading = true
      this.loadError = false
      try {
        // first page carries the aggregate status counts (they never cap at
        // the page size), which drive the tiles AND pick which queue the
        // rows come from. a naive latest-events page would bury the open
        // ones: on a mature tenant the most recent events are overwhelmingly
        // resolved (anderson imported ~2,600 of them), so the widget would
        // claim "nothing needs a response" beside a three-digit New tile.
        const { value: { data = {} } = {} } = await list_ops_desk_events(this.tenant, { limit: 1 })
        this.counts = data.counts || null
        const byStatus = (this.counts && this.counts.status) || {}
        // the list endpoint filters on a single status, so list the most
        // actionable non-empty queue; the other two stay reachable through
        // their tiles
        const rowStatus = OPEN_STATUSES.find(s => (byStatus[s] || 0) > 0)
        this.rowStatus = rowStatus || null
        if (rowStatus) {
          const { value: { data: page = {} } = {} } = await list_ops_desk_events(
            this.tenant, { status: rowStatus, limit: MAX_ROWS }
          )
          this.events = page.items || []
        } else {
          this.events = []
        }
      } catch (e) {
        this.loadError = true
      }
      this.loading = false
    },
  },
}
</script>
